import { memo, useState } from 'react';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowRightIcon,
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  cn,
  Paragraph,
} from '@quickmd-llc/design-system';
import { Events, tracking, EventAction } from '@lib';
import { Provider } from '@types';
import { ProviderDetails, useFilters } from '../';
import { getVisibleTimes, MAX_VISIBLE_TIMES, MIN_VISIBLE_TIMES } from './utils';
import TimeSlot from './TimeSlot';

interface Props {
  onSelectAvailability: (
    provider: Provider,
    time: string,
    position: number,
  ) => void;
  lastEndedVisitDateTime: string;
  position: number;
  provider: Provider;
  sameDate: boolean;
  soonest: boolean;
  timeSlotDate: string;
  times: string[];
}

function Availability({
  onSelectAvailability,
  lastEndedVisitDateTime,
  position,
  provider,
  sameDate,
  soonest,
  timeSlotDate,
  times,
}: Props): JSX.Element | null {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [, setSearchParams] = useSearchParams();

  const { setProvider, setShowFilters, isActive } = useFilters();
  const visibleTimes = getVisibleTimes(times, isExpanded);

  const showViewAllButton = isExpanded && times.length > MAX_VISIBLE_TIMES;

  function handleClickTimeSlot(time: string) {
    onSelectAvailability(provider, time, position);
  }

  function handleClickExpandToggle() {
    setIsExpanded(!isExpanded);

    tracking.trackEvent(Events.TOGGLE_PROVIDER_CARD, {
      Action: isExpanded ? EventAction.CLOSE : EventAction.OPEN,
    });
  }

  function handleClickViewAll() {
    setProvider(provider);
    setShowFilters(true);

    tracking.trackEvent(Events.SELECT_VIEW_ALL_AVAILABILITIES, {
      Provider: provider.id,
    });
  }

  function handleOpenProviderNextAvailabilities() {
    setSearchParams({ date: timeSlotDate, providerId: provider.id });
  }
  if (times.length === 0) {
    return null;
  }

  return (
    <div
      className={cn('rounded-lg bg-white p-4', {
        'border border-coastal-blue-60':
          (soonest && !isActive) || lastEndedVisitDateTime,
      })}
      data-testid={`pw_schedule-provider-${provider.lastName}`}
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <ProviderDetails
          provider={provider}
          soonest={soonest && !isActive}
          lastEndedVisitDateTime={lastEndedVisitDateTime}
          sameDate={sameDate}
        />
        {sameDate && times?.length > MIN_VISIBLE_TIMES && (
          <div className="flex-shrink-0">
            <Button
              onClick={handleClickExpandToggle}
              size="s"
              isIcon
              className="px-sp-1"
            >
              {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </Button>
          </div>
        )}
      </div>

      {sameDate ? (
        <div className="grid grid-cols-3 gap-2">
          {visibleTimes.map((time) => (
            <TimeSlot key={time} onClick={handleClickTimeSlot} time={time} />
          ))}
        </div>
      ) : (
        <Paragraph
          className="flex w-full cursor-pointer flex-row items-center justify-center gap-2 font-semibold"
          size="sm"
          onClick={handleOpenProviderNextAvailabilities}
        >
          {`View Next Availability: ${dayjs(timeSlotDate).format('ddd, MMM D')}`}
          <ArrowRightIcon />
        </Paragraph>
      )}

      {sameDate && showViewAllButton && (
        <div className="mt-4 flex justify-center">
          <Button size="s" priority="tertiary" onClick={handleClickViewAll}>
            View all availabilities <ArrowRightIcon />
          </Button>
        </div>
      )}
    </div>
  );
}

export default memo(Availability);
